import { motion, useReducedMotion } from "motion/react";
import { useLayoutEffect, useRef, useState, type ReactNode, type Ref } from "react";
import { glideTransition } from "@/lib/motion";
import { cn } from "@/lib/utils";

type Box = { x: number; scaleX: number; width: number; ready: boolean };

export function GlideTrack({
  activeKey,
  className,
  children,
}: {
  activeKey: string;
  className?: string;
  children: (register: (key: string) => Ref<HTMLButtonElement>) => ReactNode;
}) {
  const reduce = useReducedMotion();
  const frame = useRef<HTMLDivElement>(null);
  const items = useRef(new Map<string, HTMLButtonElement>());
  const baseWidth = useRef(0);
  const [box, setBox] = useState<Box>({ x: 0, scaleX: 1, width: 0, ready: false });

  const register = (key: string) => (node: HTMLButtonElement | null) => {
    if (node) items.current.set(key, node);
    else items.current.delete(key);
  };

  useLayoutEffect(() => {
    const parent = frame.current;
    const current = items.current.get(activeKey);
    if (!parent || !current) return;

    const measure = () => {
      const parentBox = parent.getBoundingClientRect();
      const itemBox = current.getBoundingClientRect();
      if (baseWidth.current === 0 && itemBox.width > 0) baseWidth.current = itemBox.width;
      const width = baseWidth.current || itemBox.width || 1;
      setBox({
        x: itemBox.left - parentBox.left + parent.scrollLeft,
        scaleX: itemBox.width / width,
        width,
        ready: true,
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(parent);
    observer.observe(current);
    return () => observer.disconnect();
  }, [activeKey]);

  return (
    <div ref={frame} className={cn("relative", className)}>
      <motion.span
        aria-hidden
        className="absolute top-1 bottom-1 left-0 z-0 rounded-pill bg-[var(--highlight)] shadow-resting"
        style={{
          width: box.width || undefined,
          transformOrigin: "left center",
          opacity: box.ready ? 1 : 0,
        }}
        initial={false}
        animate={{ x: box.x, scaleX: box.scaleX }}
        transition={glideTransition(reduce)}
      />
      {children(register)}
    </div>
  );
}
